import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CalendarPlus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAwaitingScheduling() {
  const [rows, setRows] = useState(null);
  const [fitters, setFitters] = useState([]);
  const [confirmFor, setConfirmFor] = useState(null);
  const [form, setForm] = useState({ fitment_date: "", fitment_time: "", fitter_user_id: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [r, f] = await Promise.all([
        axios.get(`${API}/admin/awaiting-scheduling`),
        axios.get(`${API}/admin/fitter-users`),
      ]);
      setRows(r.data);
      setFitters(f.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not load"); }
  };
  useEffect(() => { load(); }, []);

  const confirm = async () => {
    if (!form.fitment_date) return toast.error("Choose a fitment date");
    if (!form.fitter_user_id) return toast.error("Choose the fitter");
    setBusy(true);
    try {
      // Assign fitter + slot (with conflict validation)
      await axios.post(`${API}/admin/jobs/${confirmFor.id}/assign-fitter`, form);
      // Also set status to scheduled via the existing confirm-schedule
      await axios.post(`${API}/admin/jobs/${confirmFor.id}/confirm-schedule`, {
        fitment_date: form.fitment_date, fitment_time: form.fitment_time,
      });
      toast.success("Job scheduled and assigned");
      setConfirmFor(null);
      setForm({ fitment_date: "", fitment_time: "", fitter_user_id: "" });
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not schedule"); }
    finally { setBusy(false); }
  };

  if (!rows) return <div className="p-8 text-center"><Loader2 className="animate-spin text-[#f28a16] inline" /></div>;

  return (
    <div data-testid="awaiting-scheduling-page">
      <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Awaiting Scheduling</h1>
      <p className="text-[#3f4b52] mt-1">
        Clients who have <b>accepted</b> a quote and are ready for a fitment slot to be confirmed.
      </p>

      <div className="mt-6 space-y-3">
        {rows.length === 0 && (
          <div className="bg-white border border-dashed border-[#cfd7db] rounded-md p-10 text-center text-[#3f4b52]" data-testid="awaiting-empty">
            No accepted quotes are waiting for scheduling.
          </div>
        )}
        {rows.map((r) => {
          const isAccepted = r.client_decision === "accepted";
          return (
          <div key={r.id} data-testid={`awaiting-row-${r.id}`} className="bg-white border border-[#cfd7db] rounded-md p-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs text-[#3f4b52] font-display uppercase tracking-widest flex items-center gap-2">
                Job #{r.job_number} · {r.quotation_number || "—"} · {r.customer_code || "—"}
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${isAccepted ? "bg-[#dcfce7] text-[#166534]" : "bg-[#fef3c7] text-[#78350f]"}`}>
                  {isAccepted ? "Quote accepted" : "Direct booking"}
                </span>
              </div>
              <div className="font-display font-black text-lg text-[#111619]">
                {r.client_name} · {r.vehicle_make_model || "—"}
                {r.registration ? <span className="ml-2 text-sm font-mono text-[#f28a16]">{r.registration}</span> : null}
              </div>
              <div className="text-xs text-[#3f4b52]">
                {isAccepted
                  ? <>Accepted {r.client_decision_at ? new Date(r.client_decision_at).toLocaleString("en-ZA") : "—"} by {r.client_decision_by}</>
                  : <>Captured {r.created_at ? new Date(r.created_at).toLocaleString("en-ZA") : "—"} · {r.payment_type || "cash"}{r.insurance_company ? ` · ${r.insurance_company}` : ""}</>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to={`/admin/jobs/${r.id}`} className="text-xs text-[#f28a16] hover:underline">Open job</Link>
              <button onClick={() => setConfirmFor(r)} data-testid={`schedule-btn-${r.id}`}
                      className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-[#f28a16] text-white text-xs font-display uppercase tracking-widest">
                <CalendarPlus size={12} /> Confirm schedule
              </button>
            </div>
          </div>
        );})}
      </div>

      {confirmFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" onClick={() => setConfirmFor(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md p-5 w-full max-w-md space-y-3" data-testid="schedule-modal">
            <div className="font-display uppercase tracking-widest text-sm">Confirm schedule · Job #{confirmFor.job_number}</div>
            <label className="block">
              <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52] block mb-1">Fitment date</span>
              <input type="date" value={form.fitment_date} onChange={(e) => setForm({ ...form, fitment_date: e.target.value })}
                     data-testid="schedule-date" className="w-full h-11 border rounded-md px-3 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52] block mb-1">Fitment time</span>
              <input type="time" value={form.fitment_time} onChange={(e) => setForm({ ...form, fitment_time: e.target.value })}
                     data-testid="schedule-time" className="w-full h-11 border rounded-md px-3 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52] block mb-1">Fitter / team leader</span>
              <select value={form.fitter_user_id} onChange={(e) => setForm({ ...form, fitter_user_id: e.target.value })}
                      data-testid="schedule-fitter" className="w-full h-11 border rounded-md px-3 text-sm">
                <option value="">Choose a fitter…</option>
                {fitters.map((f) => (<option key={f.id} value={f.id}>{f.name || f.email}</option>))}
              </select>
            </label>
            <div className="flex gap-2">
              <button onClick={() => setConfirmFor(null)} className="flex-1 border rounded-md py-2 text-sm">Cancel</button>
              <button onClick={confirm} disabled={busy || !form.fitment_date}
                      data-testid="schedule-confirm"
                      className="flex-1 bg-[#f28a16] text-white rounded-md py-2 text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
                {busy ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
